/**
 * Não medicamento com ficha no mestre: complementar (padrão) ou sincronizar com o mestre (escolha do cliente).
 *   MasterSyncCard  no produto: valor atual × valor do mestre por campo, com a origem; "Sincronizar com o mestre" e
 *                   "Voltar aos meus dados".
 *   MasterSyncBulk  em lote, na lista do catálogo: prévia (quantos itens e campos mudariam) e aplicar/desfazer.
 * Medicamento e item sem ficha conferida não aparecem (o mestre impõe / nada a sincronizar).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from './ui.tsx';
import { Modal } from '../pages/CatalogSettings.tsx';

interface Field { field: string; label: string; current: string | number | null; master: string | number | null; origin: 'cliente' | 'mestre' | 'vazio'; changes: boolean }
interface Preview { items: { id: string; masterSync: boolean; fields: Field[] }[]; eligible: number; notEligible: number; changes: number }
const ORIGIN: Record<Field['origin'], [string, string]> = { cliente: ['seu dado', 'info'], mestre: ['do mestre', 'ok'], vazio: ['vazio', 'off'] };
const short = (v: unknown) => { const s = v == null ? '—' : String(v); return s.length > 140 ? `${s.slice(0, 140)}…` : s; };

function useSync(productIds: string[], key: unknown[]) {
  const qc = useQueryClient();
  const preview = useQuery({ queryKey: ['master-sync', ...key], queryFn: () => api<Preview>('/catalog/master-sync/preview', { method: 'POST', json: { productIds } }) });
  const done = () => { qc.invalidateQueries({ queryKey: ['master-sync'] }); qc.invalidateQueries({ queryKey: ['product'] }); qc.invalidateQueries({ queryKey: ['catalog'] }); };
  const sync = useMutation({ mutationFn: () => api<{ products: number; fields: number }>('/catalog/master-sync', { method: 'POST', json: { productIds } }), onSuccess: done });
  const undo = useMutation({ mutationFn: () => api<{ products: number; restored: number }>('/catalog/master-sync/undo', { method: 'POST', json: { productIds } }), onSuccess: done });
  return { preview, sync, undo };
}

export function MasterSyncCard({ productId, canEdit }: { productId: string; canEdit: boolean }) {
  const { preview, sync, undo } = useSync([productId], [productId]);
  const it = preview.data?.items[0];
  if (preview.isPending || !it || !it.fields.length) return null;
  return (
    <section className="panel">
      <div className="panel-head"><h2>Catálogo mestre</h2>
        {it.masterSync ? <Tag tone="ok">sincronizado com o mestre</Tag> : <Tag tone="info">complementar (padrão)</Tag>}</div>
      <p className="small muted">{it.masterSync
        ? 'Este item segue a ficha do mestre: melhorias da ficha chegam aqui. Se você editar um campo, só ele volta a ser seu.'
        : 'Vale o que você cadastrou; o mestre só completa o que estiver vazio. Sincronizando, os campos passam a seguir a ficha (seus valores ficam guardados).'}</p>
      <table className="simple">
        <thead><tr><th>Campo</th><th>Atual</th><th>Mestre</th><th>Origem</th></tr></thead>
        <tbody>{it.fields.map((f) => (
          <tr key={f.field}><td>{f.label}</td><td className="small" style={{ whiteSpace: 'normal', maxWidth: 260 }}>{short(f.current)}</td>
            <td className="small" style={{ whiteSpace: 'normal', maxWidth: 260, fontWeight: f.changes ? 600 : undefined }}>{short(f.master)}</td>
            <td><Tag tone={ORIGIN[f.origin][1]}>{ORIGIN[f.origin][0]}</Tag></td></tr>))}</tbody>
      </table>
      {canEdit && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button type="button" className="btn primary sm" disabled={sync.isPending || !it.fields.some((f) => f.changes || f.origin !== 'mestre')}
            onClick={() => { if (window.confirm('Sincronizar com o mestre? Os campos acima passam a seguir a ficha; seus valores ficam guardados para voltar.')) sync.mutate(); }}>Sincronizar com o mestre</button>
          {it.masterSync && <button type="button" className="btn sm" disabled={undo.isPending} onClick={() => { if (window.confirm('Voltar aos seus dados? Os valores guardados voltam.')) undo.mutate(); }}>Voltar aos meus dados</button>}
        </div>
      )}
      {(sync.error || undo.error) && <ErrorBox error={sync.error ?? undo.error} />}
    </section>
  );
}

export function MasterSyncBulk({ productIds, label, onClose }: { productIds: string[]; label: string; onClose: () => void }) {
  const { preview, sync, undo } = useSync(productIds, ['bulk', productIds.length, productIds[0]]);
  const p = preview.data;
  return (
    <Modal title={`Sincronizar com o mestre · ${label}`} onClose={onClose}>
      {preview.error ? <ErrorBox error={preview.error} /> : !p ? <Loading /> : (
        <div className="stack-y">
          <p className="small" style={{ margin: 0 }}><b>{int(p.eligible)}</b> item(ns) não medicamento com ficha no mestre; <b>{int(p.changes)}</b> campo(s) mudariam.
            {p.notEligible ? ` ${int(p.notEligible)} fica(m) de fora (medicamento, kit ou sem ficha conferida).` : ''}</p>
          <div style={{ maxHeight: 280, overflow: 'auto' }}>
            {p.items.slice(0, 30).map((i) => (
              <details key={i.id} className="small"><summary>{i.fields.find((f) => f.field === 'title')?.current ?? i.id} · {i.fields.filter((f) => f.changes).length} mudança(s){i.masterSync ? ' · já sincronizado' : ''}</summary>
                {i.fields.filter((f) => f.changes).map((f) => <div key={f.field}>{f.label}: <s>{short(f.current)}</s> → <b>{short(f.master)}</b></div>)}</details>
            ))}
          </div>
          {sync.data && <div className="notice small">{int(sync.data.products)} item(ns) sincronizado(s), {int(sync.data.fields)} campo(s).</div>}
          {undo.data && <div className="notice small">{int(undo.data.products)} item(ns) voltaram aos seus dados ({int(undo.data.restored)} campo(s)).</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn primary" disabled={sync.isPending || !p.eligible} onClick={() => sync.mutate()}>Sincronizar {int(p.eligible)} item(ns)</button>
            <button type="button" className="btn" disabled={undo.isPending} onClick={() => undo.mutate()}>Voltar aos meus dados</button>
          </div>
          {(sync.error || undo.error) && <ErrorBox error={sync.error ?? undo.error} />}
        </div>
      )}
    </Modal>
  );
}
